/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Modal to change profile password
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.3.1
 */

import React, { ChangeEvent, FormEvent, Fragment, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../../../assets';
import { Input } from '../../../../../components/common';
import { EInputKind } from '../../../../../components/common/Input/Input';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageSettings,
} from '../../../../../modules/internationalization/intSelectors';
import ICommonTranslation from '../../../../../modules/internationalization/types/common';
import { IPageSettingsTranslation } from '../../../../../modules/internationalization/types/pages/pageSettings';
import { Loader } from '../../../../../modules/loader/components/Loader';
import { CrossButtonStyled } from '../../../../../modules/modal/components/styled/ModalStyled';
import ButtonServiceUserDenied from '../../../../../modules/role/components/ButtonServiceUserDenied';
import { IRootReducer } from '../../../../../reducers/rootReducer';
import { stringFormat } from '../../../../../utils/stringFormat';
import { settingsProfileSavePasswordAction, settingsProfileSetParamPasswordAction } from '../settingsProfileActions';
import {
    ESettingsProfilePasswordError,
    ESettingsProfilePasswordFields,
    ESettingsProfileValidationCodes,
    mapSettingsProfilePasswordFieldsToErrors,
} from '../settingsProfileConstants';
import {
    makeSelectSettingsProfileIsLoadingModal,
    makeSelectSettingsProfileMetaPassword,
    makeSelectSettingsProfileParamsPassword,
} from '../settingsProfileSelectors';
import {
    ISettingsProfileMetaPassword,
    ISettingsProfileParamsPassword,
    TSettingsProfileSetParamPasswordActionPayload,
} from '../settingsProfileTypes';
import {
    SettingsProfileLabelStyled,
    SettingsProfilePopupContainerStyled,
    SettingsProfilePopupContentStyled,
    SettingsProfilePopupControlStyled,
    SettingsProfilePopupTitleStyled,
} from '../styled/SettingsProfileStyled';
import { settingsProfilePasswordValidation } from './settingsProfilePasswordValidation';

interface ISettingsProfilePasswordRenderInput {
    disabled?: boolean;
    dataName: string;
    label: string;
    passwordFieldType: ESettingsProfilePasswordFields;
}

export interface ISettingsProfilePasswordValidateParams {
    [ESettingsProfilePasswordFields.CURRENT]: string;
    [ESettingsProfilePasswordFields.NEW]: string;
    [ESettingsProfilePasswordFields.REPEAT]: string;
}

interface ISettingsProfilePasswordModalProps {
    onClose: () => void;
}


export type TSettingsProfileQuestionModalProps = ISettingsProfilePasswordModalProps & IConnectedProps & IDispatchProps;

export const SettingsProfilePasswordModal = (props: TSettingsProfileQuestionModalProps) => {
    const validateParams = settingsProfilePasswordValidation(props.meta);
    const { password: translationPassword, errorCodes } = props.settingsTranslation.profile;

    const illegalLength: string = errorCodes[ESettingsProfileValidationCodes.PASSWORD_ILLEGAL_LENGTH];
    const translationErrorCodes: {[key: string]: string} = {
        ...errorCodes,
        [ESettingsProfileValidationCodes.PASSWORD_ILLEGAL_LENGTH]: illegalLength ?
            stringFormat(illegalLength, props.meta.minLength, props.meta.maxLength) : '',
    };
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    useEffect(() => {
        if (isSubmitted && !props.isLoadingModal) {
            props.onClose();
        }
    }, [isSubmitted, props.isLoadingModal]);

    const handlePasswordCurrentChange =
        (event: ChangeEvent<HTMLInputElement>, passwordType: ESettingsProfilePasswordFields) => {
            props.setParam({ [passwordType]: event.target.value });
            handlePasswordsValidate(passwordType, event.target.value);
        };

    const handlePasswordsValidate = (passwordType: ESettingsProfilePasswordFields, value: string) => {
        const { passwordCurrent, passwordNew, passwordNewRepeat } = props.params;
        const errors: {[key in ESettingsProfilePasswordError]?: string} =
            validateParams({ passwordCurrent, passwordNew, passwordNewRepeat, [passwordType]: value });
        if (errors[ESettingsProfilePasswordError.NEW]) errors[ESettingsProfilePasswordError.REPEAT] = '';

        const payload: TSettingsProfileSetParamPasswordActionPayload = {
            [ESettingsProfilePasswordError.CURRENT]: errors[ESettingsProfilePasswordError.CURRENT] ?? '',
            [ESettingsProfilePasswordError.NEW]: errors[ESettingsProfilePasswordError.NEW] ?? '',
            [ESettingsProfilePasswordError.REPEAT]: errors[ESettingsProfilePasswordError.REPEAT] ?? '',
        };
        props.setParam(payload);
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        props.savePassword();
        setIsSubmitted(true);
    };

    const renderPasswordInput = ({ disabled, label, passwordFieldType, dataName }:
                                     ISettingsProfilePasswordRenderInput) => {
        const passwordFieldError: ESettingsProfilePasswordError =
            mapSettingsProfilePasswordFieldsToErrors[passwordFieldType];
        return (
            <Fragment>
                <SettingsProfileLabelStyled>
                    {label}
                </SettingsProfileLabelStyled>

                <Input
                    dataName={dataName}
                    disabled={disabled}
                    hintMessage={props.params[passwordFieldError] ?
                        translationErrorCodes[props.params[passwordFieldError]] : undefined}
                    inputtype='password'
                    kind={props.params[passwordFieldError] ? EInputKind.error : undefined}
                    placeholder={translationPassword.placeholder}
                    value={props.params[passwordFieldType]}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handlePasswordCurrentChange(event, passwordFieldType)}
                />
            </Fragment>
        );
    };

    const passwordNewDisabled = !props.params[ESettingsProfilePasswordFields.CURRENT];

    return (
        <SettingsProfilePopupContainerStyled data-component="changePasswordModal" onSubmit={handleSubmit}>
            <CrossButtonStyled
                alt="Close"
                src={icons.cancelIcon}
                onClick={() => props.onClose()}
            />

            <SettingsProfilePopupTitleStyled>
                {translationPassword.modalTitle}
            </SettingsProfilePopupTitleStyled>

            <SettingsProfilePopupContentStyled>
                {
                    renderPasswordInput({
                        dataName: 'currentPasswordInput',
                        label: translationPassword.labelCurrent,
                        passwordFieldType: ESettingsProfilePasswordFields.CURRENT,
                    })
                }

                {
                    renderPasswordInput({
                        dataName: 'newPasswordInput',
                        disabled: passwordNewDisabled,
                        label: translationPassword.labelNew,
                        passwordFieldType: ESettingsProfilePasswordFields.NEW,
                    })
                }

                {
                    renderPasswordInput({
                        dataName: 'repeatPasswordInput',
                        disabled: passwordNewDisabled,
                        label: translationPassword.labelNewRepeat,
                        passwordFieldType: ESettingsProfilePasswordFields.REPEAT,
                    })
                }
            </SettingsProfilePopupContentStyled>

            <SettingsProfilePopupControlStyled>
                <ButtonServiceUserDenied
                    data-name="saveButton"
                    disabled={
                        passwordNewDisabled ||
                        !!props.params[ESettingsProfilePasswordError.CURRENT] ||
                        !!props.params[ESettingsProfilePasswordError.NEW] ||
                        !!props.params[ESettingsProfilePasswordError.REPEAT]
                    }
                    kind="mediumNormal"
                    type="submit"
                >
                    {props.settingsTranslation.profile.submit}
                </ButtonServiceUserDenied>
            </SettingsProfilePopupControlStyled>

            <Loader showLoader={props.isLoadingModal}/>
        </SettingsProfilePopupContainerStyled>
    );
};


export interface IConnectedProps {
    commonTranslation: ICommonTranslation;
    isLoadingModal: boolean;
    meta: ISettingsProfileMetaPassword;
    settingsTranslation: IPageSettingsTranslation;
    params: ISettingsProfileParamsPassword;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    commonTranslation: makeSelectTranslationCommon(),
    isLoadingModal: makeSelectSettingsProfileIsLoadingModal(),
    meta: makeSelectSettingsProfileMetaPassword(),
    params: makeSelectSettingsProfileParamsPassword(),
    settingsTranslation: makeSelectTranslationPageSettings(),
});

export interface IDispatchProps {
    savePassword: () => void;
    setParam: (payload: TSettingsProfileSetParamPasswordActionPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    savePassword: () => dispatch(settingsProfileSavePasswordAction()),
    setParam: payload => dispatch(settingsProfileSetParamPasswordAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsProfilePasswordModal);
